import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild, signal } from '@angular/core';
import * as pack from '../../../../../../../package.json';
import { SquareButtonComponent } from '../square-button/square-button.component';
/**
 * ModalComponent is designed to present content within a modal dialog,
 * overlaying the main application content. It supports dynamic text content
 * and a customizable action button. The modal's visibility, text, and button
 * actions can be controlled via component inputs.
 *
 * Implements Angular's `OnChanges` lifecycle hook to react to input changes dynamically.
 *
 * Usage:
 * Include the `app-modal` selector in your component template and bind the necessary inputs.
 * You can provide a `closeAction` callback to be executed when the modal's button is clicked.
 *
 * ```html
 * <app-modal
 *  [isModalOpen]="isOpen"
 *  [modalText]="'Your custom text here'"
 *  [buttonText]="'Close'"
 *  [closeAction]="yourCloseFunction"
 * ></app-modal>
 * ```
 *
 */
@Component({
  selector: 'app-modal',
  imports: [SquareButtonComponent],
  templateUrl: './modal.component.html',
})
export class ModalComponent implements OnChanges {
  @ViewChild('dialog') dialog!: ElementRef<HTMLDialogElement>;

  @Input() type = 'app';
  @Input() phone?: string = 'aaa';
  @Input() email?: string = 'aa';

  /** Technical support contact email (club's configured contact email). */
  @Input() supportEmail = 'support@3ddigitalvenue.com';

  /** Determines if the modal is visible on the screen. */
  @Input() isModalOpen = false;

  /** The text content displayed within the modal. */
  @Input() modalText?: string = '';

  /** The text label of the action button in the modal. */
  @Input() buttonText?: string = '';

  /** The callback function that is executed when the modal's action button is clicked. */
  @Input() closeAction?: () => void = () => {};

  year = signal(new Date().getFullYear());

  public pack = pack;

  /**
   * Responds to input changes and toggles the modal's dialog display based on the `isModalOpen` state.
   */
  ngOnChanges(changes: SimpleChanges): void {
    const values = changes['isModalOpen'];

    if (values?.previousValue !== values?.currentValue) {
      this.toggleDialog();
    }
  }

  /** Toggles the dialog's display state based on the `isModalOpen` input. */
  private toggleDialog(): void {
    if (this?.isModalOpen) {
      this?.openDialog();
    } else {
      this?.closeDialog();
    }
  }

  /** Closes the dialog element. */
  private closeDialog(): void {
    this.dialog?.nativeElement.close();
  }

  /** Opens the dialog as a modal window. */
  private openDialog(): void {
    this.dialog?.nativeElement.showModal();
  }
}
